
import usePlugin from "@/plugins";
import { useTitle } from "@vueuse/core";
import {
  ApproveGetRequest,
  ApproveGetResponse,
  ApproveLv1,
  ApproveLv2,
  ApproveLv2Summary,
  ApproveSetRequest,
  ApproveSetResponse,
} from "@/types";
import { computed, defineComponent, onMounted, reactive, ref } from "vue";
import ProgressSpinner from "primevue/progressspinner";
import numeral from "numeral";
import DmeArrow from "@/components/dme/DmeArrow.vue";
import App from "@/App.vue";
import { clone } from "ramda";

const initRequest = {
  approverId: "VP1",
  approverYear: 2022,
  guId: "",
  currentQuarterOverallComment: "",
  nextQuarterOverallComment: "",
  yearOverallComment: "",
  currentQuarterOverallComment1: "",
  nextQuarterOverallComment1: "",
  yearOverallComment1: "",
  currentQuarterOverallComment2: "",
  nextQuarterOverallComment2: "",
  currentQuarterOverallComment3: "",
  nextQuarterOverallComment3: "",
  currentQuarterOverallComment4: "",
  nextQuarterOverallComment4: "",
  yearOverallComment2: "",
  yearOverallComment3: "",
  yearOverallComment4: "",
  isLoading: false,
  allComment: "",
  tabIndex: 0,
  commentToNextApprover: "",
};

export default defineComponent({
  name: "DmeApproveScreen",
  components: { ProgressSpinner, DmeArrow },
  setup() {
    const { axios, error, success, router, warning } = usePlugin();
    const request = reactive(clone(initRequest));
    const approveLv1s = ref<ApproveLv1[]>([]);
    const approveLv2s = ref<ApproveLv2[]>([]);
    const approveLv2Summary = ref<ApproveLv2Summary[]>([]);
    // const approveLv1s = useLocalStorage<ApproveLv1[]>("approveLv1s", []);
    // const approveLv2s = useLocalStorage<ApproveLv2[]>("approveLv2s", []);
    // const approveLv2Summary = useLocalStorage<ApproveLv2Summary[]>(
    //   "approveLv2Summary",
    //   []
    // );
    const hasDisplay1 = computed(
      () =>
        approveLv1s.value.length > 0 &&
        approveLv1s.value.findIndex((x) => x.previousCommentName1) >= 0
    );
    const hasDisplay2 = computed(
      () =>
        approveLv1s.value.length > 0 &&
        approveLv1s.value.findIndex((x) => x.previousCommentName2) >= 0
    );
    const hasDisplay3 = computed(
      () =>
        approveLv1s.value.length > 0 &&
        approveLv1s.value.findIndex((x) => x.previousCommentName3) >= 0
    );
    const hasDisplay4 = computed(
      () =>
        approveLv1s.value.length > 0 &&
        approveLv1s.value.findIndex((x) => x.previousCommentName4) >= 0
    );
    const hasDisplay = computed(() => hasDisplay1.value || hasDisplay2.value || hasDisplay3.value || hasDisplay4.value);

    const isVpPosition = computed(() => position.value === "VP");
    const isCeoPosition = computed(() => position.value === "CEO");

    const position = computed(() => {
      if (approveLv1s.value.length > 0) {
        return approveLv1s.value[0].approverId.replace(/[0-9]/g, "");
      }
      if (approveLv2s.value.length > 0) {
        return approveLv2s.value[0].approverId.replace(/[0-9]/g, "");
      }
      return "N/A";
    });

    const showCommentToNextApprover = computed(() => {
      return (
        approveLv2s.value.length > 0 &&
        approveLv2s.value.filter((x) => x.approveNeed).length ===
          approveLv2s.value.filter((x) => x.approveNeed && x.approveYes).length
      );
    });

    const onLoad = async (fromSave = false) => {
      try {
        request.isLoading = true;
        const result = await axios.post<ApproveGetResponse>(
          "api/v1/dme/approve/get",
          {
            approverId: request.approverId,
            approverYear: request.approverYear,
            guId: request.guId,
          } as ApproveGetRequest
        );

        //clear reactive request
        request.commentToNextApprover = "";
        request.tabIndex = 0;
        request.allComment = "";
        request.currentQuarterOverallComment = "";
        request.nextQuarterOverallComment = "";
        request.yearOverallComment = "";
        request.currentQuarterOverallComment1 = "";
        request.nextQuarterOverallComment1 = "";
        request.yearOverallComment1 = "";
        request.currentQuarterOverallComment2 = "";
        request.nextQuarterOverallComment2 = "";
        request.yearOverallComment2 = "";

        approveLv1s.value = result.data.approverOutput.approveLv1s ?? [];
        approveLv2s.value = result.data.approverOutput.approveLv2s ?? [];
        approveLv2Summary.value =
          result.data.approverOutput.approveLv2Sums ?? [];

        if (hasDisplay1.value) {
          request.currentQuarterOverallComment1 = `Overall ${
            approveLv1s.value[0].previousCommentName1
          } : ${approveLv1s.value[0].currentQuarterPreviousComment1Overall ||
            "N/A"}`;
          request.nextQuarterOverallComment1 = `Overall ${
            approveLv1s.value[0].previousCommentName1
          } : ${approveLv1s.value[0].nextQuarterPreviousComment1Overall ||
            "N/A"}`;
          request.yearOverallComment1 = `Overall ${
            approveLv1s.value[0].previousCommentName1
          } : ${approveLv1s.value[0].yearPreviousComment1Overall || "N/A"}`;
        }
        if (hasDisplay2.value) {
          request.currentQuarterOverallComment2 = `Overall ${
            approveLv1s.value[0].previousCommentName2
          } : ${approveLv1s.value[0].currentQuarterPreviousComment2Overall ||
            "N/A"}`;
          request.nextQuarterOverallComment2 = `Overall ${
            approveLv1s.value[0].previousCommentName2
          } : ${approveLv1s.value[0].nextQuarterPreviousComment2Overall ||
            "N/A"}`;
          request.yearOverallComment2 = `Overall ${
            approveLv1s.value[0].previousCommentName2
          } : ${approveLv1s.value[0].yearPreviousComment2Overall || "N/A"}`;
        }
        if (hasDisplay3.value) {
          request.currentQuarterOverallComment3 = `Overall ${
            approveLv1s.value[0].previousCommentName3
          } : ${approveLv1s.value[0].currentQuarterPreviousComment3Overall ||
            "N/A"}`;
          request.nextQuarterOverallComment3 = `Overall ${
            approveLv1s.value[0].previousCommentName3
          } : ${approveLv1s.value[0].nextQuarterPreviousComment3Overall ||
            "N/A"}`;
          request.yearOverallComment3 = `Overall ${
            approveLv1s.value[0].previousCommentName3
          } : ${approveLv1s.value[0].yearPreviousComment3Overall || "N/A"}`;
        }
        if (hasDisplay4.value) {
          request.currentQuarterOverallComment4 = `Overall ${
            approveLv1s.value[0].previousCommentName4
          } : ${approveLv1s.value[0].currentQuarterPreviousComment4Overall ||
            "N/A"}`;
          request.nextQuarterOverallComment4 = `Overall ${
            approveLv1s.value[0].previousCommentName4
          } : ${approveLv1s.value[0].nextQuarterPreviousComment4Overall ||
            "N/A"}`;
          request.yearOverallComment4 = `Overall ${
            approveLv1s.value[0].previousCommentName4
          } : ${approveLv1s.value[0].yearPreviousComment4Overall || "N/A"}`;
        }

        if (result.data.approverOutput.returns.length === 0) {
          success("Data has been loaded successfully.");
          
          if(!fromSave && !showAcceptLevel1.value && !showAcceptLevel2.value && !showAcceptLevel2Summary.value){
            warning('Sorry, you have already completed this transaction.', true)
          }
        } else {
          error(JSON.stringify(result.data.approverOutput.returns, null, 2));
        }
      } catch (e) {
        error(e);
      } finally {
        request.isLoading = false;
      }

      // approveLv1s.value.forEach((x) => {
      //   x.currentQuarter = "D";
      //   x.nextQuarter = "D";
      // });
      // approveLv2s.value.forEach((x, i) => {
      //   if (i % 2 === 0) x.approveNeed = "X";
      // });
      // approveLv2Summary.value.forEach((x) => {
      //   x.nextQuarterApproveNeed = "X";
      //   x.currentQuarterApproveNeed = "X";
      //   x.yearApproveNeed = "X";
      // });

      // fakeApprove()
    };
    const onApproveLv1 = (input: ApproveLv1, inputCase: string) => {
      if (inputCase === "current") {
        input.currentQuarterApproveYes = "X";
        input.currentQuarterApproveNo = "";
      } else if (inputCase === "next") {
        input.nextQuarterApproveYes = "X";
        input.nextQuarterApproveNo = "";
      } else if (inputCase === "year") {
        input.yearApproveYes = "X";
        input.yearApproveNo = "";
      }
    };
    const onRejectLv1 = (input: ApproveLv1, inputCase: string) => {
      if (inputCase === "current") {
        input.currentQuarterApproveYes = "";
        input.currentQuarterApproveNo = "X";
      } else if (inputCase === "next") {
        input.nextQuarterApproveYes = "";
        input.nextQuarterApproveNo = "X";
      } else if (inputCase === "year") {
        input.yearApproveYes = "";
        input.yearApproveNo = "X";
      }
    };

    const onApproveLv2 = (input: any, inputCase?: string) => {
      if (inputCase === undefined) {
        input.approveYes = "X";
        input.approveNo = "";
      } else if (inputCase === "current") {
        input.currentQuarterApproveYes = "X";
        input.currentQuarterApproveNo = "";
      } else if (inputCase === "next") {
        input.nextQuarterApproveYes = "X";
        input.nextQuarterApproveNo = "";
      }
    };
    const onRejectLv2 = (input: any, inputCase?: string) => {
      if (inputCase === undefined) {
        input.approveYes = "";
        input.approveNo = "X";
      } else if (inputCase === "current") {
        input.currentQuarterApproveYes = "";
        input.currentQuarterApproveNo = "X";
      } else if (inputCase === "next") {
        input.nextQuarterApproveYes = "";
        input.nextQuarterApproveNo = "X";
      }
    };
    const copyOverallComment = () => {
      approveLv1s.value.forEach((x) => {
        x.currentQuarterCommentOverall =
          request.currentQuarterOverallComment && x.approveNeed
            ? request.currentQuarterOverallComment
            : x.currentQuarterCommentOverall;
        x.nextQuarterCommentOverall =
          request.nextQuarterOverallComment && x.approveNeed
            ? request.nextQuarterOverallComment
            : x.nextQuarterCommentOverall;
        x.yearCommentOverall =
          request.yearOverallComment && x.approveNeed
            ? request.yearOverallComment
            : x.yearCommentOverall;
      });
    };

    const copyCommentToNextApprover = () => {
      if (
        isVpPosition.value &&
        showCommentToNextApprover.value &&
        request.commentToNextApprover
      ) {
        approveLv2s.value.forEach((x) => {
          x.comment = request.commentToNextApprover;
        });
      }
    };
    const onSubmit = async () => {
      if (
        !isLevel1Completed.value ||
        !isLevel2Completed.value ||
        !isLevel2SummaryCompleted.value
      ) {
        error("error : please complete action all tab");
        return;
      }
      copyOverallComment();
      copyCommentToNextApprover();

      try {
        request.isLoading = true;
        const result = await axios.post<ApproveSetResponse>(
          "api/v1/dme/approve/set",
          {
            approveLv1s: approveLv1s.value ?? [],
            approveLv2s: approveLv2s.value ?? [],
            approveLv2Summary: approveLv2Summary.value ?? [],
          } as ApproveSetRequest
        );
        if (result.data.bapiRet2s.length === 0) {
          success("ทำรายการสำเร็จ / transaction successful", true);
        } else {
          error(JSON.stringify(result.data.bapiRet2s, null, 2), null, true);
        }
        await onLoad(true);
      } catch (e) {
        error(e);
      } finally {
        request.isLoading = false;
      }
    };

    // const onAcceptAll = () => {
    //   request.tabIndex = 0;
    //   approveLv2s.value
    //     .filter((x) => x.approveNeed)
    //     .forEach((x) => (x.approveYes = "X"));
    //   approveLv2s.value
    //     .filter((x) => x.approveNeed)
    //     .forEach((x) => (x.approveNo = ""));
    //   approveLv2s.value
    //     .filter((x) => x.approveNeed)
    //     .forEach((x) => (x.comment = request.allComment));
    //   success("ทำการ Mark Approve ทั้งหมดแล้ว กรุณาตรวจสอบและบันทึกรายการ");
    // };

    // const onRejectAll = () => {
    //   request.tabIndex = 0;
    //   approveLv2s.value
    //     .filter((x) => x.approveNeed)
    //     .forEach((x) => (x.approveYes = ""));
    //   approveLv2s.value
    //     .filter((x) => x.approveNeed)
    //     .forEach((x) => (x.approveNo = "X"));
    //   approveLv2s.value
    //     .filter((x) => x.approveNeed)
    //     .forEach((x) => (x.comment = request.allComment));
    //   success("ทำการ Mark Reject ทั้งหมดแล้ว กรุณาตรวจสอบและบันทึกรายการ");
    // };

    const hasLevel2Approve = computed(
      () => approveLv2s.value.findIndex((x) => x.approveNeed) >= 0
    );
    const hasNormalApprove = computed(
      () => approveLv2s.value.findIndex((x) => x.approveNeed === "X") >= 0
    );

    const formatAmount = (input: number) => {
      return numeral(input).format("#,###,###.00");
    };

    const onAcceptAllLevel1 = () => {
      approveLv1s.value.forEach((x) => {
        if (x.approveNeed && x.nextQuarterApproveNeed) {
          x.nextQuarterApproveYes = "X";
          x.nextQuarterApproveNo = "";
        }
        if (x.approveNeed && x.currentQuarterApproveNeed) {
          x.currentQuarterApproveYes = "X";
          x.currentQuarterApproveNo = "";
        }
        if (x.approveNeed && x.yearApproveNeed) {
          x.yearApproveYes = "X";
          x.yearApproveNo = "";
        }
      });
    };
    const onAcceptAllLevel2 = () => {
      approveLv2s.value.forEach((x) => {
        if (x.approveNeed) {
          x.approveYes = "X";
          x.approveNo = "";
        }
      });
    };
    const onAcceptAllLevel2Summary = () => {
      approveLv2Summary.value.forEach((x) => {
        if (x.nextQuarterApproveNeed) {
          x.nextQuarterApproveYes = "X";
          x.nextQuarterApproveNo = "";
        }
        if (x.currentQuarterApproveNeed) {
          x.currentQuarterApproveYes = "X";
          x.currentQuarterApproveNo = "";
        }
      });
    };

    const showAcceptLevel1 = computed(() => {
      return approveLv1s.value.findIndex((x) => x.approveNeed) >= 0;
    });
    const showAcceptLevel2 = computed(() => {
      return approveLv2s.value.findIndex((x) => x.approveNeed) >= 0;
    });
    const showAcceptLevel2Summary = computed(() => {
      return (
        approveLv2Summary.value.findIndex(
          (x) => x.nextQuarterApproveNeed || x.currentQuarterApproveNeed
        ) >= 0
      );
    });

    const isLevel1CurrentAllAccept = computed(() => {
      return (
        approveLv1s.value.length > 0 &&
        approveLv1s.value.findIndex(
          (x) => x.approveNeed && x.currentQuarterApproveNeed
        ) >= 0 &&
        approveLv1s.value.filter(
          (x) => x.approveNeed && x.currentQuarterApproveNeed
        ).length ===
          approveLv1s.value.filter(
            (x) =>
              x.approveNeed &&
              x.currentQuarterApproveNeed &&
              x.currentQuarterApproveYes
          ).length
      );
    });
    const isLevel1NextAllAccept = computed(() => {
      return (
        approveLv1s.value.length > 0 &&
        approveLv1s.value.findIndex(
          (x) => x.approveNeed && x.nextQuarterApproveNeed
        ) >= 0 &&
        approveLv1s.value.filter(
          (x) => x.approveNeed && x.nextQuarterApproveNeed
        ).length ===
          approveLv1s.value.filter(
            (x) =>
              x.approveNeed &&
              x.nextQuarterApproveNeed &&
              x.nextQuarterApproveYes
          ).length
      );
    });
    const isLevel1YearAllAccept = computed(() => {
      return (
        approveLv1s.value.length > 0 &&
        approveLv1s.value.findIndex(
          (x) => x.approveNeed && x.yearApproveNeed
        ) >= 0 &&
        approveLv1s.value.filter((x) => x.approveNeed && x.yearApproveNeed)
          .length ===
          approveLv1s.value.filter(
            (x) => x.approveNeed && x.yearApproveNeed && x.yearApproveYes
          ).length
      );
    });

    useTitle("DME Approve");

    const isLevel1Completed = computed(() => {
      if (approveLv1s.value.length === 0) return true;
      return (
        approveLv1s.value.filter((x) => x.approveNeed && x.yearApproveNeed)
          .length ===
          approveLv1s.value.filter(
            (x) =>
              x.approveNeed &&
              x.yearApproveNeed &&
              (x.yearApproveYes || x.yearApproveNo)
          ).length &&
        approveLv1s.value.filter(
          (x) => x.approveNeed && x.currentQuarterApproveNeed
        ).length ===
          approveLv1s.value.filter(
            (x) =>
              x.approveNeed &&
              x.currentQuarterApproveNeed &&
              (x.currentQuarterApproveYes || x.currentQuarterApproveNo)
          ).length &&
        approveLv1s.value.filter(
          (x) => x.approveNeed && x.nextQuarterApproveNeed
        ).length ===
          approveLv1s.value.filter(
            (x) =>
              x.approveNeed &&
              x.nextQuarterApproveNeed &&
              (x.nextQuarterApproveYes || x.nextQuarterApproveNo)
          ).length
      );
    });
    const isLevel2Completed = computed(() => {
      if (approveLv2s.value.length === 0) return true;
      return (
        approveLv2s.value.filter((x) => x.approveNeed).length ===
        approveLv2s.value.filter(
          (x) => x.approveNeed && (x.approveYes || x.approveNo)
        ).length
      );
    });
    const isLevel2SummaryCompleted = computed(() => {
      if (approveLv2Summary.value.length === 0) return true;
      return (
        approveLv2Summary.value.filter((x) => x.nextQuarterApproveNeed)
          .length ===
          approveLv2Summary.value.filter(
            (x) =>
              x.nextQuarterApproveNeed &&
              (x.nextQuarterApproveYes || x.nextQuarterApproveNo)
          ).length &&
        approveLv2Summary.value.filter((x) => x.currentQuarterApproveNeed)
          .length ===
          approveLv2Summary.value.filter(
            (x) =>
              x.currentQuarterApproveNeed &&
              (x.currentQuarterApproveYes || x.currentQuarterApproveNo)
          ).length
      );
    });

    const queryGuId = computed(
      () => (router.currentRoute.value.query["guid"] as string) ?? ""
    );

    const rowClassLevel1Current = (row: ApproveLv1) =>
      row.approveNeed && row.currentQuarterApproveNeed
        ? "attention-require"
        : "";
    const rowClassLevel1Year = (row: ApproveLv1) =>
      row.approveNeed && row.yearApproveNeed ? "attention-require" : "";
    const rowClassLevel1Next = (row: ApproveLv1) =>
      row.approveNeed && row.nextQuarterApproveNeed ? "attention-require" : "";
    const rowClassLevel2 = (row: ApproveLv2) => {
      return {
        level1: row.wbsLevel1 && !row.wbsLevel2 && !row.wbsLevel4,
        level2: row.wbsLevel1 && row.wbsLevel2 && !row.wbsLevel4,
        level3: row.wbsLevel1 && row.wbsLevel2 && row.wbsLevel4,
        "attention-require": row.approveNeed,
      };
    };

    const rowClassLevel2SummaryCurrent = (row: ApproveLv2Summary) => {
      return {
        level1: row.wbsLevel1 && !row.wbsLevel2,
        level3: row.wbsLevel1 && row.wbsLevel2,
        "attention-require": row.currentQuarterApproveNeed,
      };
    };
    const rowClassLevel2SummaryNext = (row: ApproveLv2Summary) => {
      return {
        level1: row.wbsLevel1 && !row.wbsLevel2,
        level3: row.wbsLevel1 && row.wbsLevel2,
        "attention-require": row.nextQuarterApproveNeed,
      };
    };

    const showSelectionSection = computed(() => {
      return (
        window.origin.indexOf("api.haadthip.com") < 0 &&
        window.origin.indexOf("apiqas.haadthip.com") < 0
      );
    });

    const variancePercent = (newVal: number, oldVal: number): string => {
      const result =
        oldVal === 0 && newVal !== 0 ? 1 : (newVal - oldVal) / oldVal;
      return numeral(result).format("0,0.00%");
    };

    const level1TabName = computed(() => {
      return position.value === "COO" ||
        position.value === "CFO" ||
        position.value === "CEO"
        ? "Approval"
        : position.value === "FP" || position.value?.startsWith("COM")
        ? "Acknowledgement"
        : "รายละเอียด Lv1";
    });

    const level2TabName = computed(() => {
      return position.value === "COO" ||
        position.value === "CFO" ||
        position.value === "CEO" ||
        position.value === "FP" ||
        position.value?.startsWith("COM")
        ? "Full year details"
        : position.value === "VP"
        ? "FULL YEAR Budget"
        : "รายละเอียด Lv2";
    });

    const level2SummaryTabName = computed(() => {
      return position.value === "COO" ||
        position.value === "CFO" ||
        position.value === "CEO" ||
        position.value === "FP" ||
        position.value?.startsWith("COM")
        ? "Quarter details"
        : position.value === "VP"
        ? "Quarter budget"
        : "ภาพรวม";
    });

    const totalCurrentQuarterNew = computed(() =>
      approveLv1s.value.map((x) => x.currentQuarterNew).reduce((x, y) => x + y)
    );
    const totalCurrentQuarterOld = computed(() =>
      approveLv1s.value.map((x) => x.currentQuarterOld).reduce((x, y) => x + y)
    );
    const totalCurrentQuarterDiff = computed(
      () => totalCurrentQuarterNew.value - totalCurrentQuarterOld.value
    );

    const totalNextQuarterNew = computed(() =>
      approveLv1s.value.map((x) => x.nextQuarterNew).reduce((x, y) => x + y)
    );
    const totalNextQuarterOld = computed(() =>
      approveLv1s.value.map((x) => x.nextQuarterOld).reduce((x, y) => x + y)
    );
    const totalNextQuarterDiff = computed(
      () => totalNextQuarterNew.value - totalNextQuarterOld.value
    );

    const totalYearNew = computed(() =>
      approveLv1s.value.map((x) => x.yearNew).reduce((x, y) => x + y)
    );
    const totalYearOld = computed(() =>
      approveLv1s.value.map((x) => x.yearOld).reduce((x, y) => x + y)
    );
    const totalYearDiff = computed(
      () => totalYearNew.value - totalYearOld.value
    );

    const commentVpHeader = computed(() =>
      approveLv1s.value.length > 0 &&
      approveLv1s.value[0].previousCommentNameVp &&
      approveLv1s.value[0].previousCommentNameVp !== "N"
        ? approveLv1s.value[0].previousCommentNameVp
        : null
    );
    const comment1Header = computed(() =>
      approveLv1s.value.length > 0 &&
      approveLv1s.value[0].previousCommentName1 &&
      approveLv1s.value[0].previousCommentName1 !== "N"
        ? approveLv1s.value[0].previousCommentName1
        : null
    );
    const comment2Header = computed(() =>
      approveLv1s.value.length > 0 &&
      approveLv1s.value[0].previousCommentName2 &&
      approveLv1s.value[0].previousCommentName2 !== "N"
        ? approveLv1s.value[0].previousCommentName2
        : null
    );
    const comment3Header = computed(() =>
      approveLv1s.value.length > 0 &&
      approveLv1s.value[0].previousCommentName3 &&
      approveLv1s.value[0].previousCommentName3 !== "N"
        ? approveLv1s.value[0].previousCommentName3
        : null
    );
    const comment4Header = computed(() =>
      approveLv1s.value.length > 0 &&
      approveLv1s.value[0].previousCommentName4 &&
      approveLv1s.value[0].previousCommentName4 !== "N"
        ? approveLv1s.value[0].previousCommentName4
        : null
    );

    onMounted(async () => {
      request.guId = queryGuId.value;
      if (request.guId) {
        await onLoad();
      }
    });

    const fakeApprove = () => {
      console.log("FAKE APPROVE");
      approveLv1s.value.forEach((x) => {
        x.approveNeed = "A";
        x.yearApproveNeed = "X";
        x.currentQuarterApproveNeed = "X";
        x.nextQuarterApproveNeed = "X";
      });
      approveLv2s.value.forEach((x) => {
        if (x.wbsLevel4) {
          x.approveNeed = "X";
        }
      });
    };

    return {
      request,
      onLoad,
      approveLv1s,
      approveLv2s,
      approveLv2Summary,
      onApproveLv1,
      onRejectLv1,
      onApproveLv2,
      onRejectLv2,
      onSubmit,

      isVpPosition,
      isCeoPosition,
      showCommentToNextApprover,
      showSelectionSection,

      // onAcceptAll,
      // onRejectAll,
      onAcceptAllLevel1,
      onAcceptAllLevel2,
      onAcceptAllLevel2Summary,

      showAcceptLevel1,
      showAcceptLevel2,
      showAcceptLevel2Summary,

      isLevel1YearAllAccept,
      isLevel1CurrentAllAccept,
      isLevel1NextAllAccept,

      isLevel1Completed,
      isLevel2Completed,
      isLevel2SummaryCompleted,

      hasDisplay,
      hasDisplay1,
      hasDisplay2,
      hasDisplay3,
      hasDisplay4,
      hasLevel2Approve,
      hasNormalApprove,
      formatAmount,

      queryGuId,

      rowClassLevel1Next,
      rowClassLevel1Year,
      rowClassLevel1Current,
      rowClassLevel2SummaryNext,
      rowClassLevel2SummaryCurrent,
      rowClassLevel2,

      variancePercent,

      level1TabName,
      level2TabName,
      level2SummaryTabName,

      totalCurrentQuarterNew,
      totalCurrentQuarterOld,
      totalCurrentQuarterDiff,
      totalNextQuarterNew,
      totalNextQuarterOld,
      totalNextQuarterDiff,
      totalYearNew,
      totalYearOld,
      totalYearDiff,

      commentVpHeader,
      comment1Header,
      comment2Header,
      comment3Header,
      comment4Header,
    };
  },
});
